/**
 * BadgeShareCard — shareable PNG tier badge UI block.
 * Renders a preview of the user's current tier badge plus Download
 * and Share buttons. Pulls the personalised PNG (cookie auth) from
 * /api/stepathon/events/{slug}/badge.png?tier=<label>.
 *
 * Uses native Web Share API on mobile (one-tap to SMS / IG / X / WhatsApp);
 * falls back to clipboard with a sonner toast on desktop.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Download, Share2, Trophy } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function BadgeShareCard({ slug, eventName, tier, totalSteps }) {
  // Cache-bust on tier promotion so the preview refreshes
  const [bust, setBust] = useState(() => Date.now());

  const tierLabel = tier?.label;
  const badgeUrl = useMemo(() => {
    if (!tierLabel) return null;
    const u = new URL(`${BACKEND}/api/stepathon/events/${slug}/badge.png`);
    u.searchParams.set("tier", tierLabel);
    u.searchParams.set("_t", String(bust));
    return u.toString();
  }, [slug, tierLabel, bust]);

  if (!tier) {
    return (
      <div data-testid="badge-empty"
           className="rounded-3xl border border-purple-300/30 bg-white/5 p-6">
        <div className="inline-flex items-center gap-2 text-pink-300 text-xs uppercase tracking-widest font-bold">
          <Trophy className="w-3.5 h-3.5" /> Shareable badge
        </div>
        <p className="mt-3 text-purple-100/80 text-sm">
          Hit your first milestone tier to unlock a shareable badge you can post
          to Instagram, X, or text to a friend.
        </p>
      </div>
    );
  }

  const shareUrl = `https://glpflo.org/stepathon/${slug}`;
  const shareText = `I just hit ${tier.emoji || ""} ${tier.label} in the GLPFlo ${eventName} — ${totalSteps?.toLocaleString() || ""} steps. Walk with me.`;

  const download = async () => {
    try {
      const res = await fetch(badgeUrl, { credentials: "include" });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `glpflo-${slug}-${tier.label.toLowerCase().replace(/\s+/g, "-")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
      toast.success("Badge downloaded — save & post it!");
    } catch {
      toast.error("Could not download badge");
    }
  };

  const share = async () => {
    // Try to share as a real file so the badge image goes through the share sheet.
    try {
      const res = await fetch(badgeUrl, { credentials: "include" });
      const blob = await res.blob();
      const file = new File([blob], `glpflo-${slug}-${tier.label}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText, url: shareUrl, title: "GLPFlo Step-a-Thon" });
        return;
      }
    } catch (_) { /* fall through */ }
    // Fall back to text-only share sheet.
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url: shareUrl, title: "GLPFlo Step-a-Thon" });
        return;
      } catch (_) { /* user cancelled */ }
    }
    // Final fallback — clipboard.
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Share text copied to clipboard");
    } catch {
      toast.error("Could not share");
    }
  };

  return (
    <div data-testid="badge-share-card"
         className="rounded-3xl border border-purple-300/30 bg-white/5 p-5">
      <div className="inline-flex items-center gap-2 text-pink-300 text-xs uppercase tracking-widest font-bold">
        <Trophy className="w-3.5 h-3.5" /> Shareable badge
      </div>
      <div className="mt-3 grid sm:grid-cols-[160px,1fr] gap-4 items-center">
        <img
          src={badgeUrl}
          alt={`${tier.label} badge for ${eventName}`}
          data-testid="badge-preview-img"
          onError={() => setBust(Date.now() + 1)}
          className="w-full sm:w-40 aspect-square rounded-2xl border border-purple-300/30 object-cover shadow-lg"
        />
        <div>
          <div className="text-2xl font-extrabold">
            {tier.emoji || "🏅"} {tier.label}
          </div>
          <div className="text-xs text-purple-200/80">
            {totalSteps?.toLocaleString() || 0} steps · {eventName}
          </div>
          <p className="mt-2 text-sm text-purple-100/80">
            Every share = a new walker on the team. Post it. Tag a friend. Move them too.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="badge-download-btn" onClick={download}
                    className="bg-pink-600 hover:bg-pink-700 text-white">
              <Download className="w-4 h-4 mr-1.5" /> Download PNG
            </Button>
            <Button data-testid="badge-share-btn" onClick={share} variant="outline"
                    className="bg-white/10 hover:bg-white/20 border-purple-300/40 text-white">
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
